
import React, { useState } from 'react';
import { useStudentExams } from '@/hooks/useStudentExams';
import { Program } from '@/services/db/types';
import { Filter } from 'lucide-react';
import ExamStatistics from './ExamStatistics';
import ExamFilters from './ExamFilters';
import ExamResultsTable from './ExamResultsTable';
import AddExamButton from './AddExamButton';
import { Button } from '@/components/ui/button';
import { ExamStats } from '@/utils/exam/types';

interface ExamsTabProps {
  studentId: number | string;
  programs?: Program[];
}

const ExamsTab = ({ studentId, programs = [] }: ExamsTabProps) => {
  const {
    examResults,
    filteredResults,
    stats,
    loading,
    filters,
    updateExam,
    deleteExam,
    updateFilters,
    clearFilters
  } = useStudentExams(studentId);

  // State to control filters visibility in mobile view
  const [showFilters, setShowFilters] = useState(false);

  // Default stats object for when stats are not available
  const defaultStats: ExamStats = {
    averageScore: 0,
    highestScore: 0,
    lowestScore: 0,
    totalExams: 0,
    scoresByType: {
      monthly: { count: 0, average: 0 },
      annual: { count: 0, average: 0 },
      test: { count: 0, average: 0 },
      quiz: { count: 0, average: 0 },
      other: { count: 0, average: 0 }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center mb-6 gap-2">
        <h2 className="text-2xl font-bold">Exam Results</h2>
        <AddExamButton studentId={studentId} />
      </div>

      {/* First Row - Statistics */}
      <div className="w-full">
        <ExamStatistics 
          stats={stats || defaultStats} 
          loading={loading} 
        />
      </div>

      {/* Second Row - Filters */}
      <div className="w-full">
        {/* Mobile filters toggle */}
        <div className="block lg:hidden mb-4">
          <div className="flex justify-between items-center p-3 border rounded-md bg-muted/30">
            <span className="font-medium flex items-center">
              <Filter size={16} className="mr-2" /> Filters
            </span>
            <Button 
              className="text-primary text-sm font-medium p-0 h-auto"
              variant="link"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </div>
          
          {showFilters && (
            <div className="mt-2 p-4 border rounded-md bg-white dark:bg-slate-800">
              <ExamFilters 
                filters={filters} 
                onFilterChange={updateFilters} 
                onClearFilters={clearFilters} 
              />
            </div>
          )}
        </div>
        
        {/* Desktop filters - always visible */}
        <div className="hidden lg:block">
          <div className="border rounded-lg bg-card">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Filters</h3>
            </div>
            <div className="p-4">
              <ExamFilters 
                filters={filters} 
                onFilterChange={updateFilters} 
                onClearFilters={clearFilters} 
              />
            </div>
          </div>
        </div>
      </div>

      {/* Third Row - Results Table */}
      <div className="w-full">
        <ExamResultsTable 
          examResults={filteredResults} 
          loading={loading}
          onDelete={deleteExam}
          studentId={studentId}
        />
      </div>
    </div>
  );
};

export default ExamsTab;
